import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Theme,
  Tooltip,
  useTheme,
} from "@mui/material";
import { useIntl } from "react-intl";
import useGlobalContext from "globalProviders/GlobalDataProvider";
import { queryClient } from "index";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import BackLink from "components/BackLink";
import {
  SkuUserAgreementInfoReviewRequest,
  SkuUserAgreementPdfPreview,
} from "services/facultyPortalClient";
import { UserAgreementStatusId } from "types/common-types";

const getStyles = (theme: Theme) => ({
  headerWrapper: {
    borderRadius: 1,
    backgroundColor: theme.palette.background.default,
    ".course-header": {
      alignItems: "start",
      ".course-header-content": {
        padding: 2,
        alignItems: "start",
        h1: {
          textAlign: "left",
        },
      },
    },
  },
  rightMenuWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: 1.5,
    px: 1.5,
    pb: 1.5,
    borderRadius: 1,
    backgroundColor: theme.palette.background.default,
  },
  border: {
    display: "flex",
    flexDirection: "column",
    borderRadius: 1,
    border: `1px solid ${theme.palette.primary.light}`,
    gap: 1,
    p: 1,
  },
  pdfWrapper: {
    borderRadius: 1,
  },
  button: {
    pointerEvents: "all",
  },
  title: {
    m: 0,
  },
});

const AgreementsReviewPage = () => {
  const { formatMessage: t } = useIntl();
  const styles = getStyles(useTheme());
  const { skuId, skuUserAgreementId } = useParams();
  const { courseService, skuUserAgreementService, courseDetailsUrl } =
    useGlobalContext();
  const [file, setFile] = useState<string | null>(null);
  const [error, setError] = useState<string | null | undefined>(null);

  const {
    data: courseHeaderResponse,
    isFetching: isFetchingCourseHeader,
    isFetched: isFetchedCourseHeader,
  } = useQuery(
    ["getCourseHeader", skuUserAgreementId],
    () => courseService.getCourseHeader(skuId),
    { keepPreviousData: true }
  );

  const {
    data: skuUserAgreementResponse,
    isFetched: isFetchedSkuUserAgreement,
  } = useQuery(
    ["getSkuUserAgreement", skuUserAgreementId],
    () => skuUserAgreementService.getSkuUserAgreement(skuUserAgreementId!),

    { keepPreviousData: true }
  );

  const { data: pdfPreviewResponse } = useQuery(
    ["skuUserAgreementService.preview", skuUserAgreementId],
    () =>
      skuUserAgreementService.preview(
        new SkuUserAgreementPdfPreview({
          fileId: skuUserAgreementId!,
          organizationUserId:
            skuUserAgreementResponse?.data?.organizationUserId!,
        })
      ),

    {
      enabled: !!skuUserAgreementResponse?.data,
      keepPreviousData: true,
      onError: (error: any) => {
        setError(`${error?.message ?? ""} Error to view PDF.`);
      },
    }
  );

  useEffect(() => {
    try {
      setFile(pdfPreviewResponse?.data!);
    } catch (error) {
      setError(pdfPreviewResponse?.responseReason);
    }
  }, [
    pdfPreviewResponse?.data,
    pdfPreviewResponse?.responseCode,
    pdfPreviewResponse?.responseReason,
  ]);

  const isLoading = isFetchedCourseHeader && isFetchedSkuUserAgreement;

  const isPending =
    isFetchedSkuUserAgreement &&
    skuUserAgreementResponse?.data?.statusId === UserAgreementStatusId.Pending;

  const acceptAgreement = async () => {
    await skuUserAgreementService.saveSkuUserAgreementReview(
      new SkuUserAgreementInfoReviewRequest({
        id: skuUserAgreementId,
        statusId: UserAgreementStatusId.Accepted,
      })
    );

    queryClient.invalidateQueries(["getSkuUserAgreement", skuUserAgreementId]);
    queryClient.invalidateQueries(["getCourses"]);
  };

  return (
    <>
      {isFetchingCourseHeader ? (
        <Grid container justifyContent="center">
          <CircularProgress />
        </Grid>
      ) : (
        <>
          {isLoading && (
            <>
              <Box display="flex" alignItems="center">
                <BackLink to={courseDetailsUrl(skuId)} />
                <h3>{t({ id: "backToCourseDetails" })}</h3>
              </Box>
              <Box
                sx={styles.headerWrapper}
                dangerouslySetInnerHTML={{
                  __html: courseHeaderResponse?.renderedTemplate ?? "",
                }}
              />
              <Box>
                <Grid container spacing={2} mt={1}>
                  <Grid item xs={12} md={9}>
                    <Box sx={styles.pdfWrapper}>
                      <Box style={{ height: "100vh" }}>
                        {error && (
                          <Box pt={2} pb={2} sx={{ width: "100%" }}>
                            <Alert severity="error">{error}</Alert>
                          </Box>
                        )}
                        {file && (
                          <iframe
                            title={t({ id: "preview" })}
                            src={file}
                            style={{ width: "100%", height: "90%" }}
                          />
                        )}

                        {!file && !error && (
                          <Grid container justifyContent="center">
                            <CircularProgress />
                          </Grid>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box sx={styles.rightMenuWrapper}>
                      <h3>{t({ id: "agreementActions" })}</h3>

                      <Box sx={styles.border}>
                        <Box component="h5" sx={styles.title}>
                          {t({ id: "agreement" })}
                        </Box>
                        {isPending ? (
                          <Button
                            fullWidth
                            variant="contained"
                            onClick={acceptAgreement}
                          >
                            {t({ id: "acceptAgreement" })}
                          </Button>
                        ) : (
                          <Tooltip
                            title={t(
                              {
                                id: "acceptedAgreementContactTooltip",
                              },
                              {
                                contact:
                                  skuUserAgreementResponse?.data?.contactTooltip ??
                                  "",
                              }
                            )}
                          >
                            <Button
                              sx={styles.button}
                              fullWidth
                              variant="success"
                            >
                              {t({ id: "acceptAgreement" })}
                            </Button>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </>
      )}
    </>
  );
};

export default AgreementsReviewPage;
